import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { DrupalRESTService } from './drupal-rest.service';

export interface IncidentBannerState {
  enabled: boolean;
  message: string;
  updated_at: number | null;
  updated_by_uid: number | null;
}

const DEFAULT_STATE: IncidentBannerState = {
  enabled: false,
  message: '',
  updated_at: null,
  updated_by_uid: null,
};

const POLL_INTERVAL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class IncidentBannerService implements OnDestroy {
  private readonly state$ = new BehaviorSubject<IncidentBannerState>(DEFAULT_STATE);
  private pollSub?: Subscription;

  readonly banner$: Observable<IncidentBannerState> = this.state$.asObservable();

  constructor(private rest: DrupalRESTService) {}

  /** Kick off periodic fetches. Safe to call repeatedly (idempotent). */
  startPolling(): void {
    if (this.pollSub) {
      return;
    }
    this.pollSub = timer(0, POLL_INTERVAL_MS)
      .pipe(switchMap(() => this.rest.httpGET('/api/incident-banner')))
      .subscribe({
        next: (res) => this.applyState(res as IncidentBannerState),
        error: () => {
          /* swallow; next tick will retry */
        },
      });
  }

  /** Force an immediate refetch (e.g. on login/logout). */
  refresh(): void {
    this.rest.httpGET('/api/incident-banner').subscribe({
      next: (res) => this.applyState(res as IncidentBannerState),
      error: () => {
        /* swallow */
      },
    });
  }

  /** Super-admin write. Updates local state on success. */
  update(enabled: boolean, message: string): Observable<IncidentBannerState> {
    return this.rest
      .httpPOST('/api/incident-banner', { enabled, message })
      .pipe(tap((res) => this.applyState(res as IncidentBannerState)));
  }

  get current(): IncidentBannerState {
    return this.state$.value;
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  private applyState(res: Partial<IncidentBannerState> | null): void {
    if (!res) {
      return;
    }
    this.state$.next({ ...DEFAULT_STATE, ...res });
  }
}
